/*
 * This program is part of the OpenCms Mercury Template.
 *
 * Copyright (c) Alkacon Software GmbH & Co. KG (http://www.alkacon.com)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { _OpenCmsReinitEditButtons } from './opencms-callbacks.js';

/**
 * Definitions of the types locally used.
 *
 * NOTE: Definition may be incomplete since the HTML attribute "data-list" contains a JSON object
 * that the list object extends from. It might provide some keys not documented/typed here.
 *
 * @typedef {Object} List Wrapper for a single list.
 * @property {JQuery<HTMLElement>} $element the HTML element of the list.
 * @property {string} id the lists unique id on the page.
 * @property {string} elementId the id of the list element's content.
 * @property {boolean} loadAll flag, indicating if all items are loaded directly.
 * @property {?number[]} pageSizes the page sizes as array. Only provided in load all case.
 * @property {JQuery<HTMLElement>} $editbox HTML element representing the edit box for empty lists.
 * @property {JQuery<HTMLElement>} $entries HTML element representing the list entries shown.
 * @property {JQuery<HTMLElement>} $spinner HTML element representing the spinner shown when loading
 * @property {JQuery<HTMLElement>} $pagination HTML element where pagination is put.
 * @property {?Map<number, JQuery>} pageEntries the entries per page. Only filled in loadAll mode.
 * @property {?JQuery<HTMLElement>} $noresults HTML element where information about the empty list is placed.
 * @property {boolean} locked flag, indicating if the list is locked for reloading.
 * @property {boolean} autoload flag, indicating if the list should automatically load more items on scroll.
 * @property {boolean} notclicked flag, indicating if the "load more" button was already clicked.
 * @property {string} option pagination option, either "append" or "paginate".
 * @property {JQuery<HTMLElement>} $facets the facet elements of the list.
 * @property {string} initparams the initial search state parameters to apply on first load of the list.
 * @property {PageData} pageData the pagination state.
 * @property {PaginationCallback} paginationCallback the pagination callback function.
 *
 * @typedef {Object.<string, List>} ListMap Object holding only lists as property values.
 *
 * @typedef {Object.<string, List[]>} ListArrayMap Object holding only list arrays as property values.
 *
 * @typedef {Object.<string, HTMLElement[]>} ResetButtonsPerList Object holding list resource ids as properties and the reset buttons map as value.
 *
 * @typedef {Object.<string, string>} InitParamsPerList Object holding list resource ids as properties and the reset buttons map as value.
 *
 * @typedef {Object} ListFilter Wrapper for a list's filter.
 * @property {HTMLElement} element the HTML element of the filter.
 * @property {string} id the id attribute of the filter HTML element.
 * @property {string} elementId the id of the list element the filter belongs to.
 * @property {Object} data returns the value of the "filter" data attribute as parsed JSON object.
 * @property {Function} getResetButtons returns the reset buttons for the current filter selection.
 * @property {Function} getFilterParams returns the URL parameters for the current filter selection.
 * @property {Function} getCountFilterParams returns the URL parameters for the current filter selection with count information.
 * @property {Function} resetAll Resets all filter selections.
 * @property {Function} toggle Toggles a filter selection.
 * @property {Function} updateDirectLink Updates the direct link with the actual search state parameters.
 * @property {Function} updateFilterCounts Updates the filter counts for the current filter selection.
 * @property {Function} updateFilterFromState Updates the filter selection from the search state parameters.
 * @property {boolean} hasResetButtons flag, indicating if the filter has reset buttons shown.
 *
 * @typedef {Object.<string, ListFilter>} ListFilterMap Object holding only list filters as property values.
 *
 * @typedef {Object.<string, ListFilter[]>} ListFilterArrayMap Object holding only list filters as property values.
 *
 * @typedef {Object} PageData Holds the list's pagination data.
 * @property {boolean} reloaded flag, indicating if ????
 * @property {number} currentPage the currently shown page.
 * @property {number} pages the total number of pages.
 * @property {number} found the total number of results.
 * @property {number} start the first result to show on the page.
 * @property {number} end the last result to show on the page.
 *
 * @typedef {Object} InitWaitCallBackHandler Handler to keep track of initialization and trigger an callback when initialization finishes.
 * @property {() => void} wait start waiting for an initialization action
 * @property {() => void} ready stop waiting for an initialization action.
 *
 * @callback PaginationCallback
 * @param {PageData} pageData the current state of the list pagination.
 *
 /

// the global objects that must be passed to this module
/** @type {jQuery} jQuery object */
var jQ;
/** @type {boolean} flag, indicating if in debug mode. */
var DEBUG;
/** @type {boolean} flag, indicating if in verbose debug mode. */
var VERBOSE;

/** @type {ListMap} all initialized lists by unique instance id  */
var m_lists = {};

/** @type {ListFilterMap} all initialized list archive filters by unique instance id  */
var m_archiveFilters = {};

/** @type {ListArrayMap} groups of lists by element id (potentially more then one on a page) */
var m_listGroups = {};

/** @type {ListFilterArrayMap} groups of archive filters lists by list element id (potentially more then one on a page) */
var m_archiveFilterGroups = {};

/** @type {List[]} all auto loading lists as array for easy iteration  */
var m_autoLoadLists = [];

/** @type {boolean} flag indicating whether to scroll to the list head on filter or on page change. */
var m_flagScrollToAnchor = true;

/** @type {ResetButtonsPerList} reset buttons to display per list. */
var m_listResetButtons = {};

/** @type {Object.<string,boolean>} whether we are in the initial load state. */
var m_isInitialLoad = {};

/** @type {InitParamsPerList}  */
var m_initParams = {};

/** @type {boolean} flag, indicating if the script is initialized. */
var m_isInitialized = false;

/** @type {Filter[]} filters queued for registration until the list is initialized. */
var m_filtersToRegister = [];

/** @type {number} number of filters to register at all. */
var m_numFilters = 0;

/**
 * Splits the request parameters in a key-value map.
 * @param {string} paramstring
 * @returns {Map<string,string>} parameters as key-value map.
 */
function splitRequestParameters(paramstring) {
    var params={};
    paramstring.replace(/[?&]*([^=&]+)=([^&]*)/gi, function(str,key,value) {
        params[key] = decodeURIComponent(value);
    });
    return params;
}

/**
 * Generates the search state parameters for the provided filter.
 *
 * @param {ListFilter} filter the filter element to get the additional parameters for.
 * @returns {string} the additional search state parameters generated by the filter.
 */
// retrieves the set filters from other filters and combines the filter parameters
function getFilterParams(filter) {

    var filterGroup = m_archiveFilterGroups[filter.elementId];
    var params = "";
    if (filterGroup !== undefined) {
        for (const fi of filterGroup) {
            if (fi.data.combine && fi.id != filter.id) {
                params += fi.getFilterParams();
            }
        }
    }
    return params;
}

/**
 * Applies a list filter.
 *
 * @param {string} id the filter to apply.
 * @param {ListFilter} filter the filter.
 * @param {string} searchStateParameters the search state parameters corresponding to the filter action.
 * @param {boolean} init whether this is the initial page load
 * @returns {void}
 */
function listFilter(id, filter, searchStateParameters, init = false) {

    if (DEBUG) {
        console.info("Lists.listFilter() called elementId=" + id);
    }
    const filterGroup = m_archiveFilterGroups[id];
    // We have more than one filter element, so we can combine them and have to adjust counts
    // I.e., if in one filter element we click a category the facet counts in the other filter elements decrease
    const adjustCounts = filterGroup.length > 1;
    // check if reset buttons need to be shown.
    const hasResetButtons = m_listResetButtons[id] != undefined;
    var listGroup = m_listGroups[id];
    if (listGroup !== undefined) {
        // required list is an element on this page
        // when we do the initial load, the list is already in the correct state
        if (init !== true) {
            for (let i = 0; i < listGroup.length; i++) {
                updateInnerList(listGroup[i].id, searchStateParameters, true);
            }
        }
        if (adjustCounts || hasResetButtons) {
            updateFilterCountsAndResetButtons(listGroup[0].id, filterGroup);
        }
        // update the filter state
        updateUrlFilterStateMarker(listGroup[0], searchStateParameters);
        // update direct link
        filterGroup.forEach((fi) => {
            if (fi.updateDirectLink) {
                fi.updateDirectLink(searchStateParameters);
            }
        });
    } else if (!init) {
        var archive = m_archiveFilters[filter.id];
        // list is not on this page, check filter target attribute
        var target = archive.data.target;
        if (target !== undefined && target !== window.location.pathname && target + "index.html" !== window.location.pathname) {
            if (DEBUG) {
                console.info("Lists.listFilter() No list group found on page, trying redirect to " + target);
            }
            var params = splitRequestParameters("reloaded=true&" + searchStateParameters);
            Mercury.post(target, params);
        } else {
            console.error("Lists.listFilter() Unable to load list!\nNo dynamic list found on the page and property 'mercury.list' not set.");
        }
    }
}

/**
 * Updates the list (results) for a changed search state.
 * Search is performed on the server and results are returned according to the state parameters.
 *
 * @param {string} id the lists id.
 * @param {string} searchStateParameters the search state parameters.
 * @param {boolean} reloadEntries flag, indicating if the shown list entries should be reloaded (in contrast to appending new ones only).
 * @param {boolean} isInitialLoad flag, indicating if this is the first load for the list after the page was originally loaded.
 * @param {InitWaitCallBackHandler} waitHandler optionally to keep an initialization action waiting till the list is updated.
 * @returns {void}
 */
function updateInnerList(id, searchStateParameters, reloadEntries, isInitialLoad = false, waitHandler = undefined) {

    if (waitHandler) {
        waitHandler.wait();
    }
    searchStateParameters = searchStateParameters || "";
    reloadEntries = reloadEntries || false;
    var list = m_lists[id];

    if (DEBUG) console.info("Lists.updateInnerList() called instanceId=" + list.id + " elementId=" + list.elementId + " parameters=" + searchStateParameters);

    if ((list.ajax == null)) {
        if (DEBUG) console.warn("Lists.updateInnerList() called instanceId=" + list.id + " elementId=" + list.elementId + " parameters=" + searchStateParameters + " does not support updates since no AJAX link is provided.");
    } else {
        if (!list.locked) {
            list.locked = true;

            var ajaxOptions = "&";
            if (reloadEntries) {
                // hide the "no results found" message during search
                list.$editbox.hide();
                list.$pagination.show();
            } else {
                // fade out the load more button
                list.$element.find('.btn-append').addClass("fadeOut");
                // we don't need to calculate facets again if we do not reload all entries
                ajaxOptions = "&hideOptions=true&";
            }
            if (list.initialLoad) {
                list.$element.addClass("initial-load");
            }

            // calculate the spinner position in context to the visible list part
            var scrollTop = Mercury.windowScrollTop();
            var windowHeight = window.innerHeight;
            var elementTop = list.$element.offset().top;
            var offsetTop = scrollTop > elementTop ? (elementTop - scrollTop) * -1 : 0;
            var elementHeight = list.$element.outerHeight(true);
            var visibleHeight = Math.min(scrollTop + windowHeight, elementTop + elementHeight) - Math.max(scrollTop, elementTop);
            var spinnerPos = ((0.5 * visibleHeight) + offsetTop) / elementHeight * 100.0;

            if (false && DEBUG) console.info("Lists.updateInnerList() Spinner animation" +
                " scrollTop=" + scrollTop +
                " windowHeight=" + windowHeight +
                " elementTop=" + elementTop +
                " offsetTop=" + offsetTop +
                " elementHeight=" + elementHeight +
                " visibleHeight=" + visibleHeight +
                " spinnerPos=" + spinnerPos + "%"
            )

            // show the spinner
            if (visibleHeight > 0 || list.$noresults != null) {
                list.$spinner.css("top", spinnerPos + "%").fadeIn(250);
            }

            // get requested page
            var page = 1;
            var pageParamPos = searchStateParameters.indexOf("page=");
            if (pageParamPos >= 0) {
                var helper = searchStateParameters.substring(pageParamPos + 5);
                var pageFromParam = parseInt(helper);
                if (!isNaN(pageFromParam) && pageFromParam > 1) {
                    page = pageFromParam;
                }
                if (list.loadAll) {
                    const params = new URLSearchParams(searchStateParameters);
                    params.delete('page');
                    searchStateParameters = params.toString();
                }
            }
            if (DEBUG) console.info("Lists.updateInnerList() showing page " + page);

            const shouldLoadMultiplePages = isInitialLoad && !list.reloadEntries && page > 1 && list.option === 'append' && !list.loadAll;
            if (shouldLoadMultiplePages) {
                const params = new URLSearchParams(searchStateParameters);
                loadMultiplePages(list, ajaxOptions, params, 1, page, waitHandler);
            } else {
                if (waitHandler) {
                    waitHandler.wait();
                }
                jQ.get(buildAjaxLink(list, ajaxOptions, searchStateParameters), function (ajaxListHtml) {
                    generateListHtml(list, reloadEntries, ajaxListHtml, page, isInitialLoad, waitHandler);
                }, "html");
            }
        }
        if (waitHandler) {
            waitHandler.ready();
        }
    }
}

/**
 * Loads all pages from the current to the last one.
 * This function should only be used for a list with append as option that does not load all entries at once
 * but initially should load more than the first page.
 *
 * @param {List} list the list to generate the link for.
 * @param {string} ajaxOptions the ajax options to pass with the link as parameters.
 * @param {URLSearchParams} searchStateParameters the search state parameters to pass with the link.
 * @param {number} currentPage the page to load next
 * @param {number} lastPage the last page to load
 * @param {InitWaitCallBackHandler} waitHandler optional init wait handler.
 * @returns {string} the AJAX link.
 */
function loadMultiplePages(list, ajaxOptions, searchStateParameters, currentPage, lastPage, waitHandler) {
    if(currentPage <= lastPage) {
        searchStateParameters.set('page', currentPage);
        if(waitHandler) waitHandler.wait();
        jQ.get(buildAjaxLink(list, ajaxOptions, searchStateParameters.toString()), function(ajaxListHtml) {
            generateListHtml(list, false, ajaxListHtml, currentPage, true, waitHandler);
            if(list.pageData && list.pageData.pages && list.pageData.pages > currentPage && currentPage < lastPage) {
                loadMultiplePages(list, ajaxOptions, searchStateParameters, currentPage+1, lastPage);
            }
        }, "html");
    }
}
/**
 * Generates the AJAX link to call to retrieve the list entries and pagination for the
 * provided state.
 *
 * @param {List} list the list to generate the link for.
 * @param {string} ajaxOptions the ajax options to pass with the link as parameters.
 * @param {string} searchStateParameters the search state parameters to pass with the link.
 * @returns {string} the AJAX link.
 */
function buildAjaxLink(list, ajaxOptions, searchStateParameters) {

    if (DEBUG) console.info("Lists.buildAjaxLink() called searchStateParameters='" + searchStateParameters + "' ajaxOptions='" + ajaxOptions + "'");

    var params = "contentpath=" + list.path
        + "&instanceId="
        + list.id
        + "&elementId="
        + list.elementId
        + "&sitepath="
        + list.sitepath
        + "&subsite="
        + list.subsite
        + "&__locale="
        + list.locale
        + "&loc="
        + list.locale
        + "&option="
        + list.option;

    if (list.$facets.length != 0) {
        // The first option is only used by the old lists. NG lists use the settings.
        params = params + "&facets=" + list.$facets.data("facets");
        params = params + list.$facets.data("settings");
    }
    return list.ajax + (list.ajax.indexOf('?') >= 0 ? '&' : '?') + params + ajaxOptions + searchStateParameters;
}

/**
 * Makes the list's HTML (results and pagination) visible on the client.
 * Call it after new results are returned from the server.
 *
 * @param {List} list the list to render the HTML for.
 * @param {boolean} reloadEntries flag, indicating if the current results should be reloaded/replaced.
 * @param {string} listHtml the list HTML as received from the server.
 * @param {number} page the number of the result page to show.
 * @param {boolean} isInitialLoad flag, indicating if the generation happens during the initial page load.
 * @param {InitWaitCallBackHandler} waitHandler optional init wait handler.
 * @returns {void}
*/
function generateListHtml(list, reloadEntries, listHtml, page, isInitialLoad = false, waitHandler = undefined) {

    if (DEBUG) console.info("Lists.generateListHtml() called");

    var $result = jQ(listHtml);
    // collect information about the search result
    var resultData = $result.find('#resultdata').first().data('result');
    list.pageData = resultData;
    list.pageData.itemsPerPage = parseInt(list.itemsPerPage, 10);
    if (DEBUG) console.info("Lists.generateListHtml() Search result - list=" + list.id + ", reloaded=" + list.pageData.reloaded + ", start=" + list.pageData.start + ", end=" + list.pageData.end + ", entries=" + list.pageData.found + ", pages=" + list.pageData.pages + ", currentPage=" + list.pageData.currentPage + ", page to show=" + page);

    var $newEntries;
    var $groups = $result.find("div[data-listgroup]");
    var hasGroups = $groups.length > 0;
    if (hasGroups) {
        // the search results should be grouped
        if (DEBUG) console.info("Lists.generateListHtml() Search result - list=" + list.id + " contains " + $groups.length + " groups");
        hasGroups = combineGroups($groups, false);
        $newEntries = $result.find(".list-entry:parent");
    } else {
        // no grouping of search results required
        $newEntries = $result.find(".list-entry:not(:empty)");
        // :not(:empty) added to remove 'invalid' decoys
    }

    if (list.loadAll) {
        list.pageEntries = paginateEntries(list, $newEntries);
        list.pageData.pages = list.pageEntries.size;
    }
    // clear the pagination element
    list.$pagination.empty();

    // initial list load:
    // entries are already there, if there are no groups in the result we can skip the reload
    // but never skip if all items are displayed
    // neither we can skip if the initparams do not correspond to the ones that the list expected
    var skipInitialLoad = list.initialLoad && !hasGroups && !list.loadAll && page == 1 && list.initparams === m_initParams[list.elementId];
    list.initialLoad = false;
    if (DEBUG && skipInitialLoad) console.info("Lists.generateListHtml() Skipping initial reload for list=" + list.id);

    if (reloadEntries && !skipInitialLoad) {
        // set min-height of list to avoid screen flicker
        list.$entries.css("min-height", list.$entries.height() + 'px');
        // remove the old entries when list is reloaded
        list.$entries.empty();
    }

    // add the new elements to the list and set pagination element with new content.
    if (list.loadAll) {
        if (list.pageEntries.size == 0) {
            list.$entries.hide();
            if (list.$noresults != null) {
                var $noResultsElements = $result.find(".list-no-entries");
                if ($noResultsElements.length > 0) {
                    list.$noresults.empty();
                    $noResultsElements.appendTo(list.$noresults)
                }
                list.$noresults.show();
            }
        } else {
            if ((page < 1) || (page > list.pageData.pages)) {
                page = 1;
            }
            if (list.$noresults != null) list.$noresults.hide();
            if ((page > 1) && (list.option === 'append')) {
                list.$entries.empty();
                for (var i=1; i<page; i++) {
                    list.pageEntries.get(i).appendTo(list.$entries);
                }
            }
            list.pageEntries.get(page).appendTo(list.$entries);
            if (list.pageEntries.size > 1) {
                var paginationString = generatePagination(list, page);
                if (!paginationString.empty) {
                    jQ(paginationString).appendTo(list.$pagination);
                }
            } else {
                updatePageData(list, page);
            }
        }
    } else {
        if (!skipInitialLoad) {
            if (page == 1) {
                list.$entries.empty();
            }
            $newEntries.appendTo(list.$entries);
        }
        // set pagination element with new content
        $result.find('.list-append-position').appendTo(list.$pagination);
    }

    var $facetOptions = list.$facets;

    if (reloadEntries || isInitialLoad && page === 1) {
        // reset the list option box
        $facetOptions.find(".list-options").remove();
        $result.find(".list-options").appendTo($facetOptions);
    }
    if (reloadEntries) {

        // check if we have found any results
        if ($newEntries.length == 0) {
            // show the "no results found" message
            list.$editbox.show();
            // no results means we don't need any pagination element
            list.$pagination.hide();
        }
        // reset the min-height of the list now that the elements are visible
        list.$entries.animate({'min-height': "0px"}, 500, function() {
            // make sure that entries are shown if the list was empty before
            list.$entries.show();
        });
    }

    setTimeout(function() {
        // trigger "list:loaded" event with a short delay, so that other lists have a change to initialize as well
        jQ('#' + list.id)[0].dispatchEvent(new CustomEvent("list:loaded", { bubbles: true, cancelable: true }));
    }, 100);

    // fade out the spinner
    list.$spinner.fadeOut(250);
    list.$element.removeClass("initial-load");
    list.locked = false;

    if ((list.appendOption === "clickfirst") && list.notclicked && !reloadEntries) {
        // this is a auto loading list that is activated on first click
        m_autoLoadLists.push(list);
        list.notclicked = false;
        if (m_autoLoadLists.length == 1) {
            // enable scroll listener because we now have one autoloading gallery
            window.addEventListener("scroll", handleAutoLoaders, { passive: true });
        }
        handleAutoLoaders();
    } else if (reloadEntries && list.autoload) {
        // check if we can render more of this automatic loading list
        handleAutoLoaders();
    }

    // there may be media elements in the list
    Mercury.update('#' + list.id);

    if (resultData.reloaded == "true" && reloadEntries && !isInitialLoad) {
        if (!list.$element.visible()) {
            if (DEBUG) console.info("Lists.generateListHtml() Scrolling to anchor");
            if (m_flagScrollToAnchor) {
                Mercury.scrollToAnchor(list.$element, -20);
            }
        }
    }

    updateURLPageMarker(list,page);
    // We have to wait till Animations finished.
    if(waitHandler) setTimeout(waitHandler.ready, 750);
    // initial load is finished
    m_isInitialLoad[list.id] = false;
}

/**
 * Replaces the current URL by setting an url parameter keeping the current page of the list in edit mode.
 * Otherwise replacing the parameters value in the history state to allow to directly set the page again when using history back.
 *
 * @param {List} list the list to update the page data for.
 * @param {number} page the new current page.
 */
function updateURLPageMarker(list, page) {
    if (DEBUG) console.info("Lists.updateURLPageMarker() called");
    const paramName = 'p_' + list.elementId;
    if(Mercury.isEditMode()) {
        const currentUrl = new URL(window.location.href);
        const currentParams = currentUrl.searchParams;
        if(page > 1) {
            currentParams.set(paramName, page);
        } else if (currentParams.has(paramName)) {
            currentParams.delete(paramName);
        }
        // We could either use pushState or replaceState.
        // It depends if each page switch should be kept in the history
        window.history.replaceState(window.history.state, null, currentUrl.toString());
    } else {
        const state = window.history.state ? window.history.state : {};
        if(page > 1) {
            state[paramName] = page.toString();
        } else if (state[paramName]) {
            delete state[paramName];
        }
        window.history.replaceState(state, null, window.location.href);
    }
}

/**
 * Replaces the current URL by setting an url parameter keeping the current filter state of the list in edit mode.
 * Otherwise replacing the parameters value in the history state to allow to directly set the the filter state again when using history back.
 *
 * @param {List} list the list to update the page data for.
 * @param {string} searchStateParameters the current search state parameters.
 */
function updateUrlFilterStateMarker(list, searchStateParameters) {
    if (DEBUG) console.info("Lists.updateUrlFilterStateMarker() called");
    const paramName = 'f_' + list.elementId;
    if(Mercury.isEditMode()) {
        const currentUrl = new URL(window.location.href);
        const currentParams = currentUrl.searchParams;
        if(searchStateParameters) {
            currentParams.set(paramName, searchStateParameters);
        } else if (currentParams.has(paramName)) {
            currentParams.delete(paramName);
        }
        // We could either use pushState or replaceState.
        // It depends if each page switch should be kept in the history
        window.history.replaceState(window.history.state, null, currentUrl.toString());
    } else {
        const state = window.history.state ? window.history.state : {};
        if(searchStateParameters) {
            state[paramName] = searchStateParameters;
        } else if (state[paramName]) {
            delete state[paramName];
        }
        window.history.replaceState(state, null, window.location.href);
    }
}

/**
 * @param {List} list the list to update the page data for.
 * @param {number} page the new current page.
 */
function updatePageData(list, page) {
    var pageData = list.pageData;
    var previousEnd = 0;
    for(var i = 1; i < page; i++) {
        previousEnd = previousEnd + getPageSize(i, list.pageSizes);
    }
    if (list.option !== 'append') {
        pageData.start = previousEnd + 1;
    } else {
        pageData.start = 1;
    }
    pageData.currentPage = page;
    pageData.end = previousEnd + getPageSize(page, list.pageSizes);
    if (pageData.end > pageData.found) {
        pageData.end = pageData.found;
    }

    updateURLPageMarker(list,page);
    if (null != list.paginationCallback) {
        list.paginationCallback(pageData);
    }
}

/**
 * Generates the pagination on the client side.
 * This is only used when all items are prefetched.
 * @param {Object} list the list (object) to generate the pagination for.
 * @param {number} page the current page
 * @returns {string | null} the HTML to render for the pagination, or null if no pagination should be rendered.
 */
function generatePagination(list, page) {

    updatePageData(list, page);
    var pagination = list.paginationInfo;
    var messages = pagination.messages;
    var listId = list.id;
    var result = [];
    var lastPage = list.pageEntries.size;
    if (lastPage > 1) {
        if (list.option === 'paginate') {
            // show the pagination
            var pagesToShow = 5;
            var firstShownPage;
            var lastShownPage;
            var isShowAll = lastPage <= pagesToShow;
            if (isShowAll) {
                firstShownPage = 1;
                lastShownPage = pagesToShow;
            } else {
                firstShownPage = page - 2 <= 1 ? 1 :  page - 2;
                lastPage = list.pageEntries.size;
                lastShownPage = firstShownPage + pagesToShow - 1;
            }
            if (lastShownPage > lastPage) {
                var diffPages = lastShownPage - lastPage;
                firstShownPage = firstShownPage - diffPages <= 1 ? 1 : firstShownPage - diffPages;
                lastShownPage = lastPage;
            }
            result.push('<ul class="pagination">');
            // previous page and first page
            result.push(generatePaginationItem("previous", page <= 1, false, page <= 1 ? 1 : page -1, messages.tpp, null, "ico fa fa-angle-left", listId)); // mercury:icon
            if (firstShownPage > 1) {
                var liClassesFirstPage = "first";
                if (firstShownPage > 2) {
                    liClassesFirstPage += " gap";
                }
                result.push(generatePaginationItem(liClassesFirstPage, page <= 1, false, 1, messages.tfp, "{{p}}", null, listId));
            }
            for (var p = firstShownPage; p <= lastShownPage; p++) {
                result.push(generatePaginationItem(p == lastShownPage ? "lastpage" : "page", false, page == p, p, messages.tp, messages.lp, "number", listId));
            }
            result.push(generatePaginationItem("next", page >= lastPage, false, page < lastPage ? page + 1 : lastPage, messages.tnp, null, "ico fa fa-angle-right", listId)); // mercury:icon
        } else if (list.option === 'append' && page < lastPage) {
            // show the button to append more results.
            result.push('<div class="list-append-position" data-dynamic="false">');
            result.push('<button class="btn btn-append" onclick="DynamicList.appendPage(\'');
            result.push(listId);
            result.push('\',');
            result.push(page + 1);
            result.push(')"><span>');
            result.push(messages.la);
            result.push("</span></button></div>");
        }
    }
    return result.empty ? null : result.join('');
}

/**
 * Renders a single pagination entry.
 * @param {string} liClasses classes for the li element additionally to active or disabled.
 * @param {boolean} isDisabled flag, indicating if the entry is disabled.
 * @param {boolean} isActive flag, indicating if the entry is active.
 * @param {string} liClasses the classes to put on the li item (except active and disabled).
 * @param {number} page the page to go to when the entry is clicked.
 * @param {string} title the (hover) title of the entry, can contain macro "{{p}}" that will be resolved to the page number of the page to go to on click.
 * @param {string} label the label of the entry, can contain macro "{{p}}" that will be resolved to the page number of the page to go to on click.
 * @param {string} spanClasses the classes to put on the &lt;span&gt; contained in the list entry.
 * @param {string} listId the id of the list the pagination entry is generated for.
 * @returns {string} the HTML to render for the pagination item.
 */
function generatePaginationItem(liClasses, isDisabled, isActive, page, title, label, spanClasses, listId) {
    /** @type {string[]} the HTML to return. The array is joined at the end. This performs better than plain string concatination.*/
    var result = []
    var resolvedTitle = title.replace(/\{\{p\}\}/g,page);
    result.push('<li');
    var classes = "";
    if (isDisabled) classes +=" disabled";
    if (isActive) classes +=" active";
    if (!(liClasses == null)) classes = " " + liClasses + classes;
    if (classes.length > 0) {
        result.push(' class="');
        result.push(classes.substring(1));
        result.push('"');
    }
    result.push('><a href="javascript:void(0)"');
    if(isDisabled) {
        result.push(' tabindex="-1"');
    }
    result.push('onclick="DynamicList.switchPage(\'');
    result.push(listId);
    result.push('\',');
    result.push(page);
    result.push(')" title="');
    result.push(resolvedTitle);
    result.push('">');
    var noLabel = (label == null);
    if (noLabel) {
        result.push('<span class="visually-hidden">');
        result.push(resolvedTitle);
        result.push('</span>');
    }
    result.push('<span class="');
    result.push(spanClasses);
    result.push('"');
    if(noLabel) {
        result.push(' aria-hidden="true"');
    }
    result.push('>');
    if(!noLabel) {
        result.push(label.replace(/\{\{p\}\}/g, page));
    }
    result.push('</span></a></li>');
    return result.join('');
}

/**
 * To support grouping of list elements do the following:
 *
 * 1. For grouping, you need a markup structure like this:
 *    <div>
 *          Some repeating content here that should appear only one, like a date etc.
 *          <div class="${the_group_id}">
 *              Content here will will be grouped.
 *          </div>
 *    <div>
 *
 *    Consider the following example:
 *    <div class="list-entry">
 *          <div data-listgroup="xy-1">
 *              Content from group 1.
 *          </div>
 *    <div>
 *    <div>
 *          <div data-listgroup="xy-2">
 *              Content from group 2.
 *          </div>
 *    <div>
 *
 *    This will be grouped like this:
 *     <div class="list-entry">
 *          <div>
 *              Content from group 1.
 *          </div>
 *          <div>
 *              Content from group 2.
 *          </div>
 *    <div>
 *
 * 2. In the formatter of the list element, mark the div to be extracted
 *    like this: <div class="list-group" data-listgroup="${some_group_id}">.
 *    The group ID must be created in the formatter.
 *    It must be the same for each element that belongs to the same group.
 *
 * 3. In the formatter XML settings, include the following configuration:
 *      <Setting>
 *        <PropertyName><![CDATA[requiredListWrapper]]></PropertyName>
 *        <Widget><![CDATA[hidden]]></Widget>
 *        <Default><![CDATA[list-with-groups]]></Default>
 *        <Visibility><![CDATA[parent]]></Visibility>
 *      </Setting>
 *
 * @param {JQuery} $groups The groups to manipulate (combine) the HTML for.
 * @param {boolean} isStatic flag, indicating if the rendered list is a static or dynamic list.
 *
 */
function combineGroups($groups, isStatic) {
    isStatic = isStatic || false;
    if (DEBUG) console.info("Lists.combineGroups() Combining list with " + $groups.length + " groups for a " + (isStatic ? "static" : "dynamic") + " list" );
    var lastGroupId, $lastGroup;
    var hasGroups = false;
    $groups.each(function(index) {
        var $this = jQ(this);
        var thisGroupId = $this.attr("data-listgroup");
        $this.removeAttr("data-listgroup");
        var $thisListEntry = $this.parents(".list-entry");
        if (Mercury.isEditMode()) {
            // reshuffle edit points
            var $editpointstart = $thisListEntry.find(".oc-editable");
            var $editpointend = $thisListEntry.find(".oc-editable-end");
            $this.prepend($editpointstart);
            $this.append($editpointend);
        }
        if (thisGroupId != lastGroupId) {
            // start of a new group
            $lastGroup = $this.parent();
            lastGroupId = thisGroupId;
        } else {
            // append to current group
            hasGroups = true;
            $this.appendTo($lastGroup);
            if (isStatic) {
                // must handle HTML differently for static lists
                $thisListEntry.remove();
            } else {
                $thisListEntry.empty();
            }
        }
    });
    return hasGroups;
}

/**
 * Splits the list of entries into several pages.
 * This is only used if all entries are loaded directly and pagination is done by the client.
 *
 * @param {List} list the list to split the entries for. It contains the pagination information.
 * @param {JQuery} $entries all list entries (this might be the groups of entries already where each group is counted as one entry).
 * @returns {Map<number, JQuery>} the map from page number to entries of the page.
 */
function paginateEntries(list, $entries) {
    var pageEntries = new Map();
    var pageSizes = list.pageSizes;
    var numEntries = $entries.length;
    var currentPage = 1;
    var pageStart = 0;
    while (pageStart < numEntries) {
        var pageSize = getPageSize(currentPage, pageSizes);
        pageEntries.set(currentPage, $entries.slice(pageStart, pageSize + pageStart)); //does it work when the end index is gt the entries length?
        pageStart = pageStart + pageSize;
        currentPage++;
    }
    return pageEntries;
}

/**
 * Returns the size of the provided page.
 *
 * @param {number} page the page to get the size for.
 * @param {number[]} pageSizes the page sizes array.
 */
function getPageSize(page, pageSizes) {
    if (page > pageSizes.length) {
        return pageSizes[pageSizes.length - 1];
    } else {
        return pageSizes[page-1];
    }
}

/**
 * Handles loading of a additional entries for lists that automatically load more entries on scroll.
 * NOTE: works currently only for lists where not all entries are loaded upfront.
 */
function handleAutoLoaders() {

    if (m_autoLoadLists != null) {
        for (var i=0; i<m_autoLoadLists.length; i++) {

            var list = m_autoLoadLists[i];
            var appendPosition = list.$element.find(".list-append-position");

            if (appendPosition.length
                && !list.locked
                && appendPosition.data("dynamic")
                // NOTE: jQuery.visible() is defined in jquery-extensions.js
                && appendPosition.visible()) {

                updateInnerList(list.id, list.$element.find('.btn-append').attr('data-load'), false);
            }
        }
    }
}

/**
 * Updates the counts on list filters.
 * Search is performed on the server and results are returned according to the state parameters.
 *
 * @param {sting} id id of the list to update.
 * @param {ListFilter[]} filterGroup the list filters to update.
 * @returns {void}
 */
function updateFilterCountsAndResetButtons(id, filterGroup) {
    if (DEBUG) {
        console.info(
            "Lists.updateFilterCountsAndResetButtons() called with filterGroup, filter, searchStateParameters:"
        );
    }
    let list = m_lists[id];
    /** @type {HTMLElement[]} */
    let resetButtons = [];
    const updateCounts = list.ajaxCount !== undefined;
    const updateResets = m_listResetButtons[list.elementId] !== undefined;
    if (!updateCounts && DEBUG) {
        console.warn(
            "Lists.updateFilterCountsAndResetButtons() does not support updates since no AJAX link for count updates is provided."
        );
    }
    if (!updateResets && DEBUG) {
        console.warn(
            "Lists.updateFilterCountsAndResetButtons() does not need to update reset buttons."
        );
    }
    if (updateCounts || updateResets) {
        let params = "&reloaded";
        for (const fi of filterGroup) {
            if (fi.data.combine) {
                if (updateCounts) {
                    params += fi.getCountFilterParams();
                }
            }
            if (updateResets) {
                resetButtons = resetButtons.concat(fi.getResetButtons());
            }
        }
        if (updateCounts) {
            jQ.get(
                buildAjaxCountLink(list, params),
                function(ajaxCountJson) {
                    replaceFilterCounts(filterGroup, ajaxCountJson);
                },
                "json"
            );
        }
        if (updateResets) {
            updateResetButtonsInternal(list.elementId, resetButtons);
        }
    }
}

/**
 * Renders the reset buttons.
 * @param {string} id
 * @param {ResetButtonsMap} resetButtons
 */
function updateResetButtonsInternal(id, resetButtons) {

    m_listResetButtons[id] = resetButtons;
    const filters = m_archiveFilterGroups[id];
    filters.forEach((filter) => {
        /** @type {HTMLElement} */
        const buttonElement = document.getElementById('resetbuttons_' + filter.id);
        if (buttonElement) {
            buttonElement.textContent = '';
            resetButtons.forEach((button) => {
                buttonElement.appendChild(button)
            });
        }
    });
}

/**
 * Generates the AJAX link to call to retrieve the item counts for filters for the
 * provided state.
 *
 * @param {List} list the list to generate the link for.
 * @param {string} searchStateParameters the search state parameters to pass with the link.
 * @returns {string} the AJAX link.
 */
function buildAjaxCountLink(list, searchStateParameters) {

    if (DEBUG) console.info("Lists.buildAjaxCountLink() called searchStateParameters='" + searchStateParameters + "'");

    var params = "contentpath=" + list.path
        + "&sitepath="
        + list.sitepath
        + "&subsite="
        + list.subsite
        + "&__locale="
        + list.locale;

    return list.ajaxCount + (list.ajaxCount.indexOf('?') >= 0 ? '&' : '?') + params + searchStateParameters;
}

/**
 * Updates the counts on list filters.
 * Callback after the counts are returned from the server.
 *
 * @param {ListFilter[]} filterGroup the list filters to update.
 * @param {Object} ajaxCountJson the new counts of the filters.
 * @returns {void}
 */
function replaceFilterCounts(filterGroup, ajaxCountJson) {

    if (DEBUG) {
        console.info("Lists.replaceFilterCounts() called with ajaxCountJson", ajaxCountJson);
    }
    filterGroup.forEach((filter) => {
        var elementFacets = ajaxCountJson[filter.id];
        if (elementFacets) {
            filter.updateFilterCounts(elementFacets);
        }
    })
}

/**
 * The callback for scroll events that tracks the current scroll position.
 * @type {() => void}
 */
const scrollListener = () => {
    const scrollPos = window.scrollY;
    if (VERBOSE) console.log("Lists.scrollListener: scrolled to " + scrollPos);
    if (Mercury.isEditMode()) {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('_sp', scrollPos);
        window.history.replaceState(window.history.state, null, currentUrl.toString());
    } else {
        const state = window.history.state ? window.history.state : {};
        if (VERBOSE) console.log('Lists.scrollListener: pulled state', state);
        state.sp = scrollPos;
        if (VERBOSE) console.log('Lists.scrollListener: pushed state', state);
        window.history.replaceState(state, null, window.history.url)
    }
}

let debScrollListener;

/**
 * Initializes scroll position tracking.
 * The function should be called if all dynamic page loading has finished.
 * It directly scrolls to the latest tracked scroll position (if any) and starts
 * tracking positions again.
 */
function initScrollPositionTracking() {
    /**
     * @type {Object}
     */
    if (DEBUG) console.info('Lists.initScrollPositionTracking() called');
    // Prevent browser scroll mechanism
    window.history.scrollRestoration = 'manual';
    let spInt = undefined;
    if (Mercury.isEditMode()) {
        const url = new URL(window.location.href);
        if (url.searchParams.has('_sp')) {
            const sp = url.searchParams.get('_sp');
            spInt = parseInt(sp);
        }
    } else {
        const state = window.history.state;
        if (DEBUG) console.log('Lists.initScrollPositionTracking: pulled state', state);
        if (state && state.sp) {
            const sp = state.sp;
            spInt = parseInt(sp);
        }
    }
    if (spInt && !isNaN(spInt) && spInt > 0) {
        window.scrollTo(0, spInt);
    }

    debScrollListener = Mercury.debounce(function() {
        scrollListener();
    }, 100);

    window.addEventListener("scroll", debScrollListener, { passive: true });
}

/**
 * Returns an init wait callback handler that will trigger the
 * provided callback when initialization is finished.
 * @param {() => void} callback the callback to trigger when initialization is finished.
 * @returns {InitWaitCallBackHandler} the callback handler that will trigger the provided callback after initialization.
 */
function getInitWaitCallBackHandler(callback) {
    let counter = 0;
    return {
        wait: () => {
            counter++;
            if (VERBOSE) console.info('Lists.getInitWaitCallBackHandler() increase counter to:' + counter);
        },
        ready: () => {
            counter--;
            if (VERBOSE) console.info('Lists.getInitWaitCallBackHandler() decrease counter to:' + counter);
            if (counter <= 0) {
                if (DEBUG) console.info('Lists.getInitWaitCallBackHandler() ready, triggering callback');
                callback();
            }
        }
    }
}

/**
 * Handler for dom changes.
 * We need to find out when the content editor is closed and
 * a reload trigger is placed in the page.
 * If this is the case, we have to prevent scroll tracking to
 * keep the former scroll position until the page is reloaded.
 *
 * @param {[MutationRecord]} m the list of dom mutations.
 */
function onDomChange(m) {
    if (m.find((mr) => mr.target.classList && mr.target.classList.contains('org-opencms-ade-contenteditor-client-css-I_CmsLayoutBundle-I_CmsXmlEditorCss-basePanel'))) {
        window.removeEventListener("scroll", debScrollListener, { passive: true });
    }
}


/**
 * Registers list filters.
 * @param {ListFilter[]} filters the list filter to register
 */
function registerFilters(filters) {

    const reloadFiltersFor = new Set();
    filters.forEach(filter => {
        if (filter.data === undefined) {
            console.error("DynamicList.registerFilters() filter found without data, ignoring!");
            return;
        }
        const listId = filter.elementId;
        // store filter data in global array
        m_archiveFilters[filter.id] = filter;
        // store filter in global group array
        var group = m_archiveFilterGroups[listId];
        if (group !== undefined) {
            group.push(filter);
        } else {
            m_archiveFilterGroups[listId] = [filter];
        }
        if (filter.hasResetButtons && m_listResetButtons[listId] == undefined) {
        // tell list, that it has to track reset buttons
        m_listResetButtons[listId] = [];
        }
        const listInitParams = m_initParams[listId];
        if (listInitParams !== undefined) {
            if (listInitParams !== '' || listInitParams !== filter.data.initparams) {
                reloadFiltersFor.add(listId);
                filter.updateFilterFromState(new URLSearchParams(listInitParams));
            }
        } else if (m_listGroups[listId] === undefined && filter.data.initparams !== undefined && filter.data.initparams != "") {
            m_initParams[listId] = filter.data.initparams;
            reloadFiltersFor.add(listId);
            if (DEBUG) {
                console.info("DynamicList.registerFilters() data filter without list on the page init params - " + filter.data.initparams);
            }
        }
    });
    reloadFiltersFor.forEach(listId => listFilter(listId, null, m_initParams[listId], true));
}

/****** Exported functions ******/

/**
 * Creates a reset button for an archive filter.
 * @param {ListFilter} filter the filter
 * @param {HTMLElement} toggle the element toggling the filter selection
 * @param {string} className extra class name for the filter button
 * @returns {HTMLElement} the reset button
 */
export function generateResetButton(filter, toggle, className) {

    const result = document.createElement("button");
    const label = toggle.dataset.label;
    const titleAttr = filter.data.resetbuttontitle;
    result.classList.add("resetbutton");
    result.classList.add(className);
    result.textContent = label ? label : toggle.id;
    if (titleAttr) {
        result.setAttribute('title', titleAttr);
    }
    result.addEventListener("click", (event) => {
        event.preventDefault();
        archiveFilter(filter, toggle.id);
    });
    return result;
}

/**
 * Creates a reset button for an archive search.
 * @param {ListFilter} filter the list filter
 * @param {HTMLElement} input the input element
 * @returns {HTMLElement} the reset button
 */
export function generateInputFieldResetButton(filter, input) {

    const result = document.createElement("button");
    const titleAttr = filter.data.resetbuttontitle;
    result.classList.add("resetbutton");
    result.classList.add("textsearch");
    if (titleAttr) {
        result.setAttribute('title', titleAttr);
    }
    const labelPlain = input.dataset.label;
    const label = labelPlain ? labelPlain.replace('%(query)', input.value) : input.value;
    result.textContent = label;
    result.addEventListener("click", (event) => {
        event.preventDefault();
        input.value = "";
        archiveSearch(filter);
    });
    return result;
}

/**
 * Registers a list filter.
 * @param {ListFilter} filter the list filter to register
 */
export function registerFilter(filter) {

    if(DEBUG) console.info("DynamicList.registerFilter() called for filter " + filter.id);
    if (filter.data === undefined) {
        console.error("DynamicList.registerFilter() filter found without data, ignoring!");
        return;
    }

    m_filtersToRegister.push(filter);
    if (m_isInitialized && m_numFilters <= m_filtersToRegister.length) {
        registerFilters(m_filtersToRegister);
    }
    return filter;
}

/**
 * Updates the counts and reset buttons.
 * Use the function only if really necessary, e.g., if a lazily loaded filter has to add more
 * filters when it is completely loaded.
 *
 * @param {string} elementId element id of the list to update the reset buttons for.
 */
export function updateFilters(elementId) {
    const filterGroup = m_archiveFilterGroups[elementId];
    const listGroup = m_listGroups[elementId];
    if (listGroup && listGroup.length > 0)
        updateFilterCountsAndResetButtons(listGroup[0].id, filterGroup);
}

/**
 * Applies a facet filter to a list.
 *
 * @param {string} id the id of the list the filter belongs to.
 * @param {string} triggerId the id of the HTML element that triggered the filter action.
 * @param {string} searchStateParameters the search state parameters representing the filter action.
 */
export function facetFilter(id, triggerId, searchStateParameters) {

    const filterGroup = m_archiveFilterGroups[id];
    for (const filter of filterGroup) {
        filter.resetAll(true);
    }
    listFilter(id, null, searchStateParameters);
}

/**
 * Applies an archive filter option to the list.
 *
 * @param {ListFilter} filter the list filter
 * @param {string} triggerId the id of the HTML element that triggered the filter action.
 */
export function archiveFilter(filter, triggerId) {

    const filterGroup = m_archiveFilterGroups[filter.elementId];
    // if this is a combined filter, get the parameters of the other combined filters in the group
    const additionalFilters = filter.data.combine ? getFilterParams(filter) : "";
    // if this is not a combined filter, reset all other filters in the group
    if (!filter.data.combine) {
        for (const fi of filterGroup) {
            if (fi.id !== filter.id) {
                fi.resetAll(true);
            }
        }
    }
    // in any case, reset all non-combined filters
    for (const fi of filterGroup) {
        if (fi.id !== filter.id && !fi.data.combine) {
            fi.resetAll(true);
        }
    }
    // calculate the filter query part for the just selected item
    const additionalStateParameter = filter.getFilterParams(false, triggerId);
    filter.toggle(triggerId);
    listFilter(filter.elementId, filter, filter.data.searchstatebase + additionalFilters + additionalStateParameter);
}


/**
 * Applies an query filter to the list.
 *
 * @param {ListFilter} filter the parent list filter the text search belongs to.
 */
export function archiveSearch(filter) {

    const filterGroup = m_archiveFilterGroups[filter.elementId];
    // we do never combine the text search when we change the search word, thus reset all other filters of the group.
    for (const fi of filterGroup) {
        fi.resetAll(false);
    }
    const additionalStateParameter = filter.getFilterParams();
    listFilter(filter.elementId, filter, additionalStateParameter);
}

/**
 * Switches to the given page. The method can only be used when all results are preloaded directly.
 *
 * @param {string} id the id of the list where the page should be appended to.
 * @param {number} page the number of the page to append.
 */
export function switchPage(id, page) {
    var list = m_lists[id];
    list.$entries.empty();
    list.pageEntries.get(page).appendTo(list.$entries);
    list.$pagination.empty();
    list.pageData.start = 1 + (page - 1) * list.itemsPerPage;
    var paginationString = generatePagination(list, page);
    if (!paginationString.empty) {
        jQ(paginationString).appendTo(list.$pagination);
    }

    // there may be media elements in the list
    Mercury.update('#' + list.id);
    if (!list.$element.visible()) {
        if (DEBUG) console.info("Lists.switchPage() - scrolling to anchor");
        if (m_flagScrollToAnchor) {
            Mercury.scrollToAnchor(list.$element, -20);
        }
    }
}

/**
 * Appends a further page of items to the currently shown items.
 * The method can only be used if all items are already preloaded.
 *
 * @param {string} id the id of the list where the page should be appended to.
 * @param {number} page the number of the page to append.
 */
export function appendPage(id, page) {
    var list = m_lists[id];
    list.pageEntries.get(page).appendTo(list.$entries);
    list.$pagination.empty();
    var paginationString = generatePagination(list, page);
    if (!paginationString.empty) {
        jQ(paginationString).appendTo(list.$pagination);
    }
    // there may be media elements in the list
    Mercury.update('#' + list.id);
}

/**
 * Update the list in the case where not all items are already loaded.
 * This will update the list according to the provided search state parameters.
 * A call to the server is made to fetch the entries according to the parameters.
 * This method is e.g., called when the page is switched for a list where the entries
 * are not preloaded completely.
 *
 * @param {string} id the id of the list for which the update should take place.
 * @param {string} searchStateParameters the search state parameters to use for the update.
 * @param {boolean} reloadEntries a flag, indicating if the currently shown results should be reloaded/replaced (or if new results should only be appended).
 */
export function update(id, searchStateParameters, reloadEntries) {

    updateInnerList(id, searchStateParameters, reloadEntries == "true", m_isInitialLoad[id]);
}

/**
 * This method can be used to allow pagination for the lists that retrieve their results not via the default AJAX call.
 *
 * @param {string} id the id of the list for which the update should take place.
 * @param {string} searchStateParameters the search state parameters to use for the update.
 * @param {boolean} reloadEntries a flag, indicating if the currently shown results should be reloaded/replaced (or if new results should only be appended).
 * @param {ListCallback} [paginationCallback] a callback triggered when a page is loaded or appended.
 */
export function injectResults(id, resultHtml, paginationCallback, pageToShow) {

    if (DEBUG) console.info("Lists.injectResults() id=" + id + ", pageToShow=" + pageToShow);
    pageToShow = pageToShow || 1;
    var list = m_lists[id];
    list.paginationCallback = paginationCallback;
    generateListHtml(list, true, resultHtml, pageToShow);
}

/**
 * Initialize the list script.
 * @param {jQuery} jQuery jQuery object.
 * @param {boolean} debug a flag, determining iff in debug mode.
 */
export function init(jQuery, debug, verbose) {

    jQ = jQuery;
    DEBUG = debug;
    VERBOSE = verbose;

    if (DEBUG) console.info("Lists.init()");

    let waitHandler = undefined;
    if (document.querySelector('.list-content') != null) {
        waitHandler = getInitWaitCallBackHandler(initScrollPositionTracking);
        waitHandler.wait();

        if (Mercury.isEditMode()) {
            if (DEBUG) console.info("Lists.init() observing DOM in edit mode.");
            const observeDOM = (function () {
                const MutationObserver = window.MutationObserver || window.WebKitMutationObserver;

                return function (obj, callback) {
                    if (!obj || obj.nodeType !== 1) return;

                    if (MutationObserver) {
                        // define a new observer
                        const mutationObserver = new MutationObserver(callback)

                        // have the observer observe for changes in children
                        mutationObserver.observe(obj, { childList: true, subtree: true })
                        return mutationObserver
                    }

                    // browser support fallback
                    else if (window.addEventListener) {
                        obj.addEventListener('DOMNodeInserted', callback, false)
                        obj.addEventListener('DOMNodeRemoved', callback, false)
                    }
                }
            })();
            observeDOM(document.body, onDomChange);
        }
    }

    m_numFilters = document.querySelectorAll("[data-filter]").length;
    if(DEBUG) console.log("Lists.init() [data-filter] elements found:" + m_numFilters);
    var $listElements = jQ('.list-dynamic');
    if (DEBUG) console.info("Lists.init() .list-dynamic elements found: " + $listElements.length);

    const urlParams = (new URL(window.location.href)).searchParams;
    if ($listElements.length > 0 ) {
        $listElements.each(function() {

            // initialize lists with values from data attributes
            var $list = jQ(this);

            if (typeof $list.data("list") !== 'undefined') {
                // read list data
                /** @type {List} a single list. */
                var list = $list.data("list");
                // add more data to list
                list.$element = $list;
                list.id = $list.attr("id");
                list.elementId = $list.data("id");
                // read and store the page size information
                var pageSizes = list.itemsPerPage;
                var sizeArrayNum = [];
                if (pageSizes !== undefined) {
                    var sizeArrayString = pageSizes.split("-");
                    sizeArrayString.forEach(function (numString) {
                        sizeArrayNum.push(Number(numString)) })
                }
                if (sizeArrayNum.length == 0) {
                    sizeArrayNum.push(5); // default size
                }
                list.pageSizes = sizeArrayNum;
                list.$editbox = $list.find(".list-editbox");
                list.$entries = $list.find(".list-entries");
                list.$spinner = $list.find(".list-spinner");
                list.$pagination = $list.find(".list-pagination");
                list.$noresults = $list.find(".list-noresults");
                list.initialLoad = true;
                list.locked = false;
                list.autoload = false;
                list.notclicked = true;
                let screenSize = Mercury.gridInfo().grid == 'xxl' ? 'xl' : Mercury.gridInfo().grid;
                if (list.appendSwitch.indexOf(screenSize) >= 0) {
                    // I think this is a cool way for checking the screen size ;)
                    list.option = "append";
                    $list.removeClass("list-paginate").addClass("list-append");
                } else {
                    list.option = "paginate";
                    $list.removeClass("list-append").addClass("list-paginate");
                }
                if (DEBUG) console.info("Lists.init() List data found - id=" + list.id + ", elementId=" + list.elementId + " option=" + list.option);
                if ((list.option === "append") && (list.appendOption === "noclick")) {
                    // list automatically loads in scrolling
                    list.autoload = true;
                    m_autoLoadLists.push(list);
                };
                list.$facets = jQ("#facets_" + list.elementId);
                // store list data in global array
                m_lists[list.id] = list;
                // store list in global group array
                var group = m_listGroups[list.elementId];
                if (typeof group !== 'undefined') {
                    group.push(list);
                } else {
                    m_listGroups[list.elementId] = [list];
                }
            }

            var initParams = "";
            if (typeof list.initparams !== "undefined") {
                initParams = list.initparams;
                if (DEBUG) console.info("Lists.init() Data init params - " + initParams);
            }

            // load the initial list

            // Check if we used the back button and have an already filtered list
            let filterState = undefined;
            const filterParam = 'f_' + list.elementId;
            let filterStr = undefined;
            if(Mercury.isEditMode()) {
                // In edit mode we need to use request parameters, since the form editor does not leave the page
                // and we want to get the former state when we close it
                if(urlParams.has(filterParam)) {
                    filterStr = urlParams.get(filterParam);
                }
            } else {
                // If we are not in edit mode, we use the history state to remember the latest filter selection
                const state = window.history.state ? window.history.state : {};
                filterStr = state[filterParam];
            }
            if(filterStr != undefined) {
                    initParams = filterStr;
            }

            // page parameter is handled separately, so we store the init params before we handle it.
            m_initParams[list.elementId] = initParams;

            const pageParam = 'p_' + list.elementId;
            let pageStr = undefined;
            if(Mercury.isEditMode()) {
                if(urlParams.has(pageParam)) {
                    pageStr = urlParams.get(pageParam);
                }
            } else {
                const state = window.history.state ? window.history.state : {};
                pageStr = state[pageParam];
            }
            if(pageStr) {
                const page = parseInt(pageStr);
                if(!isNaN(page) && page > 1) {
                    initParams = 'page=' + page + (initParams == '' ? '' : ('&' + initParams));
                }
            }

            updateInnerList(list.id, initParams, true, true, waitHandler);
        });

        if (m_autoLoadLists.length > 0) {
            // only enable scroll listener if we have at least one autoloading gallery
            window.addEventListener("scroll", handleAutoLoaders, { passive: true });
        }

        // unfold filterboxes if on desktop and responsive setting is used
        document.querySelectorAll(".filterbox .collapse").forEach((element) => {
            if (DEBUG) {
                console.info("Lists.init() collapse elements found for filter id " + element.id);
            }
            if ((element.classList.contains("op-lg") && Mercury.gridInfo().isMinLg())
                || (element.classList.contains("op-md") && Mercury.gridInfo().isMinMd())
                || (element.classList.contains("op-sm") && Mercury.gridInfo().isMinSm())) {
                if (typeof jQ === "function" && typeof jQ(element).collapse === "function") {
                    jQ(element).collapse("show");
                }
            }
        });
    }

    var $staticGroupListElements = jQ('.type-static-list .list-with-groups.list-entries');
    if (DEBUG) console.info("Lists.init() .type-static-list .list-with-groups elements found: " + $staticGroupListElements.length);

    if ($staticGroupListElements.length > 0 ) {
        $staticGroupListElements.each(function() {

            // rewrite content of each list group
            var $list = jQ(this);
            var $clone = $list.clone();

            var $groups = $clone.find("div[data-listgroup]");
            if ($groups.length > 0) {
                // the search results should be grouped
                if (DEBUG) console.info("Lists.init() Found static list with " + $groups.length + " groups");
                combineGroups($groups, true);
                $list.replaceWith($clone);
            }
        });
        _OpenCmsReinitEditButtons(DEBUG);
    }

    m_isInitialized = true;
    if (waitHandler) waitHandler.ready();
    if(m_filtersToRegister >= m_numFilters) {
        registerFilters(m_filtersToRegister);
    }
}

export function setFlagScrollToAnchor(flagScrollToAnchor) {

    m_flagScrollToAnchor = !!flagScrollToAnchor;
}
